import { Injectable, WritableSignal, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, map, of, shareReplay } from 'rxjs';

import { environment } from '@environments/environment';
import { Globals } from '@core/globals';
import { GlobalsService } from '@services/globals.service';
import { LanguageService } from '@services/language.service';
import { FilterDataResponse, FilterDataMessage } from '@models/filter-data.model';
import { ListBanner, ListBannersResponse } from '@models/list.model';

@Injectable({
  providedIn: 'root',
})
export class FilterDataService {
  private http = inject(HttpClient);
  private globals = inject(Globals);
  private globalsService = inject(GlobalsService);
  private languageService = inject(LanguageService);
  public filterData: WritableSignal<FilterDataMessage | undefined> = signal(undefined);
  public bannersList: WritableSignal<ListBanner[] | undefined> = signal(undefined);
  public bodyTypeWithImages: string[] = ['C', 'H', 'MPV', 'S', 'SUV', 'V', 'B', 'CAB', 'CPE'];
  public filterLanguage?: 'cs';

  getFilter(): Observable<FilterDataMessage> {
    if (!this.filterData() || this.filterLanguage !== this.languageService.language()) {
      this.filterLanguage = this.languageService.language();
      return this.http.get<FilterDataResponse>(`${environment.filterApi}`, { headers: this.globalsService.headerHash() }).pipe(
        map((res) => {
          if (!res) return {} as FilterDataMessage;

          const response = res.message;
          if (!response) return {} as FilterDataMessage;
          for (let bodyType of response.bodyType) {
            if (this.bodyTypeWithImages.includes(bodyType.id)) bodyType.img = 'images/body-type/' + bodyType.id.toLowerCase() + '.svg';
          }
          response.leasingAdvanceSteps = response.leasingAdvance?.map((advance) => advance.id);
          response.monthlyPaymentSteps = response.monthlyPaymentFrom?.map((payment) => payment.id);
          response.leasingNumberOfMonthSteps = response.leasingNumberOfMonth?.map((month) => month.id);
          response.priceSteps = [];
          const priceLastValue = response.priceFrom[response.priceFrom.length - 2].id + 200000;
          const priceLast = { id: priceLastValue, slug: priceLastValue, title: 'Neomezená', infinite: true };
          let priceToCopy = [...response.priceTo];
          priceToCopy.pop();
          priceToCopy.push(priceLast);
          for (let price of priceToCopy) {
            response.priceSteps.push(price.id);
          }
          response.priceTo = priceToCopy;

          response.make.forEach((make) => {
            make.titleWithoutDiacritics = this.globalsService.removeDiacritics(make.title);
          });

          response.buyoutMake.forEach((make) => {
            make.titleWithoutDiacritics = this.globalsService.removeDiacritics(make.title);
          });

          response.yearSteps = [];
          for (let year of response.yearFrom) {
            response.yearSteps.push(year.id);
          }

          response.mileageSteps = [];
          for (let mileage of response.mileageFrom) {
            response.mileageSteps.push(mileage.id);
          }

          response.performanceSteps = [];
          for (let performance of response.performanceFrom) {
            response.performanceSteps.push(performance.id);
          }

          response.color.sort((a: any, b: any) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });
          this.filterData.set(response);
          return response;
        }),
        shareReplay(1),
      );
    } else {
      return of(this.filterData() as FilterDataMessage);
    }
  }

  getBannersList(): Observable<ListBanner[] | undefined> {
    if (this.bannersList() !== undefined) return of(this.bannersList());

    return this.http.get<ListBannersResponse>(environment.bannersList).pipe(
      map((response) => {
        const banners = response[this.globals.language()].items;
        this.bannersList.set(banners);
        return banners;
      }),
    );
  }
}
